'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Sparkles, MapPin } from 'lucide-react';

import { useLanguage } from '@/context/LanguageContext';

export default function MarketingFooter() {
  const { t } = useLanguage();
  const hp = t?.homePage;
  const fl = t?.footerLinks;

  const footerLinks = {
    [hp?.footerPlatform ?? 'Platform']: [
      { label: t?.nav?.aiTools ?? 'AI Tools', href: '/ai-tools-page' },
      { label: t?.nav?.create ?? 'Create', href: '/create-page' },
      { label: t?.nav?.projects ?? 'Projects', href: '/projects-page' },
      { label: fl?.templates ?? 'Templates', href: '/templates' },
    ],
    [hp?.footerCompany ?? 'Company']: [
      { label: t?.footer?.about ?? 'About Us', href: '/about' },
      { label: t?.footer?.blog ?? 'Blog', href: '/blog' },
      { label: t?.footer?.careers ?? 'Careers', href: '/careers' },
    ],
    [hp?.footerLegal ?? 'Legal']: [
      { label: t?.legal?.privacy ?? 'Privacy Policy', href: '/privacy-policy' },
      { label: t?.legal?.terms ?? 'Terms of Service', href: '/terms-of-service' },
      { label: t?.legal?.refund ?? 'Refund Policy', href: '/refund-policy' },
      { label: t?.legal?.cookies ?? 'Cookie Policy', href: '/cookie-policy' },
      { label: fl?.aiUsageGuidelines ?? 'AI Usage Guidelines', href: '/ai-usage-guidelines' },
      { label: fl?.contentSafetyPolicy ?? 'Content Safety Policy', href: '/content-safety-policy' },
      { label: fl?.copyrightDmca ?? 'Copyright / DMCA', href: '/terms-of-service#copyright-dmca-notices' },
      { label: t?.legal?.security ?? 'Security', href: '/security' },
    ],
    [hp?.footerSupport ?? 'Support']: [
      { label: fl?.learnCenter ?? 'Learn Center', href: '/learn-center' },
      { label: t?.nav?.pricing ?? 'Pricing', href: '/pricing' },
      { label: fl?.helpCenter ?? 'Help Center', href: '/help-center' },
      { label: t?.legal?.resources ?? 'Resources', href: '/resources' },
      { label: t?.footer?.contact ?? 'Contact', href: '/contact' },
    ],
  };

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 mb-12">
          <div className="lg:col-span-2">
            <Link
              href="/"
              className="flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity"
            >
              <span className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 shrink-0 overflow-hidden">
                <span className="sm:hidden">
                  <AppLogo size={56} />
                </span>
                <span className="hidden sm:block">
                  <AppLogo size={64} />
                </span>
              </span>
              <span className="font-bold text-xl sm:text-2xl text-foreground tracking-tight">
                AIMaker Studio
              </span>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3 max-w-xs">
              {hp?.footerTagline ??
                'AIMakerStudio helps people turn dreams into organized projects, brand assets, visual concepts, launch plans, and real opportunities.'}
            </p>
            <p className="text-xs text-muted-foreground mb-1">
              {hp?.footerOwner ?? 'AIMaker Studio is a product of'}{' '}
              <span className="text-foreground/70 font-500">
                {hp?.footerOwnerName ?? 'BeMel Productions LLC'}
              </span>
              .
            </p>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-5">
              <MapPin size={11} className="text-muted-foreground/60 shrink-0" />
              {hp?.footerLocation ?? 'AIMakerStudio · Powered by DreamDNA™'}
            </p>
          </div>

          {Object.entries(footerLinks)?.map(([section, links]) => (
            <div key={`footer-section-${section}`}>
              <p className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-4">
                {section}
              </p>
              <ul className="flex flex-col gap-2.5">
                {links?.map((link) => (
                  <li key={`footer-link-${link?.label}`}>
                    <Link
                      href={link?.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-border">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-3">
            <div className="flex flex-col gap-1">
              <p className="text-xs text-muted-foreground">
                {hp?.footerCopyright ?? '© 2026'}{' '}
                <span className="text-foreground/70 font-500">
                  {hp?.footerOwnerName ?? 'AIMakerStudio'}
                </span>
                . AIMakerStudio™. {hp?.footerAllRights ?? 'All rights reserved.'}
              </p>
              <p className="text-xs text-muted-foreground">
                {hp?.footerFullCredit ?? 'AIMakerStudio — Powered by DreamDNA™'}
              </p>
              <p className="text-xs text-muted-foreground/50 leading-relaxed max-w-xl">
                {hp?.footerProprietaryMarks ??
                  'AIMakerStudio™, Dream DNA Studio™, Dream DNA™, DreamScore™, Dream Gallery™, and related platform concepts are proprietary marks and methodologies of BeMel Productions LLC.'}
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <Link
                href="/privacy-policy"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {t?.legal?.privacy ?? 'Privacy Policy'}
              </Link>
              <Link
                href="/terms-of-service"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {t?.legal?.terms ?? 'Terms of Service'}
              </Link>
              <Link
                href="/terms-of-service#copyright-dmca-notices"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {fl?.copyrightDmca ?? 'Copyright / DMCA'}
              </Link>
              <Link
                href="/about"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {t?.footer?.about ?? 'About Us'}
              </Link>
            </div>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Sparkles size={12} className="text-primary" />
            {hp?.footerDisclaimer ??
              'Users are responsible for reviewing all generated content before publishing.'}
          </p>
        </div>
      </div>
    </footer>
  );
}
